import React, { useState } from 'react';
import { bankGuaranteesEnhancedAPI } from '../../../services/bgFinanceService';
import { emptyRenewForm } from './bgShared';
import { toStoredAttachments } from '../../../utils/attachmentStorage';
import { notifyError, notifySuccess, notifyWarning } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';

const BgRenewModal = ({ bg, onClose, onRenewed }) => {
  const [form, setForm] = useState(() => emptyRenewForm(bg));
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setForm((p) => ({ ...p, attachments: [...(p.attachments || []), ...newFiles] }));
    } finally {
      setUploading(false);
    }
    event.target.value = '';
  };

  const handleSubmit = async () => {
    if (!form.new_expiry_date) {
      notifyWarning('New expiry date is required.');
      return;
    }
    if (!form.new_amount || Number(form.new_amount) <= 0) {
      notifyWarning('New amount must be greater than 0.');
      return;
    }
    if (!(await confirmDialog(`Renew ${bg.bg_no}? Previous expiry history will be preserved.`, 'Renew BG / Bond'))) return;

    setSaving(true);
    try {
      const payload = {
        renewal_reference_no: form.renewal_reference_no || null,
        new_expiry_date: form.new_expiry_date,
        new_claim_expiry_date: form.new_claim_expiry_date || null,
        new_amount: Number(form.new_amount),
        renewal_date: form.renewal_date || null,
        renewal_charges: form.renewal_charges !== '' ? Number(form.renewal_charges) : null,
        commission_amount: form.commission_amount !== '' ? Number(form.commission_amount) : null,
        premium_amount: form.premium_amount !== '' ? Number(form.premium_amount) : null,
        remarks: form.remarks || null,
        attachments: form.attachments || [],
      };
      const res = await bankGuaranteesEnhancedAPI.renew(bg.id, payload);
      notifySuccess('BG / Bond renewed successfully.');
      onRenewed?.(res?.data);
      onClose();
    } catch (err) {
      notifyError(err?.response?.data?.message || 'Unable to renew BG / Bond.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <h2>Renew BG / Bond — {bg.bg_no}</h2>
          <button type="button" className="btn-close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <div className="form-row">
            <div className="form-group"><label>Renewal Reference No.</label><input value={form.renewal_reference_no} onChange={(e) => setForm((p) => ({ ...p, renewal_reference_no: e.target.value }))} /></div>
            <div className="form-group"><label>Renewal Date</label><input type="date" value={form.renewal_date} onChange={(e) => setForm((p) => ({ ...p, renewal_date: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Previous Expiry Date</label><input type="date" value={form.previous_expiry_date} readOnly /></div>
            <div className="form-group"><label>New Expiry Date <span className="req">*</span></label><input type="date" value={form.new_expiry_date} onChange={(e) => setForm((p) => ({ ...p, new_expiry_date: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Previous Claim Expiry</label><input type="date" value={form.previous_claim_expiry_date} readOnly /></div>
            <div className="form-group"><label>New Claim Expiry Date</label><input type="date" value={form.new_claim_expiry_date} onChange={(e) => setForm((p) => ({ ...p, new_claim_expiry_date: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Previous Amount</label><input value={form.previous_amount} readOnly /></div>
            <div className="form-group"><label>New Amount <span className="req">*</span></label><input type="number" value={form.new_amount} onChange={(e) => setForm((p) => ({ ...p, new_amount: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Renewal Charges</label><input type="number" value={form.renewal_charges} onChange={(e) => setForm((p) => ({ ...p, renewal_charges: e.target.value }))} /></div>
            <div className="form-group"><label>Bank Commission</label><input type="number" value={form.commission_amount} onChange={(e) => setForm((p) => ({ ...p, commission_amount: e.target.value }))} /></div>
          </div>
          <div className="form-row">
            <div className="form-group"><label>Premium Amount</label><input type="number" value={form.premium_amount} onChange={(e) => setForm((p) => ({ ...p, premium_amount: e.target.value }))} /></div>
          </div>
          <div className="form-group"><label>Remarks</label><textarea rows={2} value={form.remarks} onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))} /></div>
          <div className="form-group">
            <label>Attachments</label>
            <input type="file" multiple accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={uploading} />
          </div>
          <p style={{ fontSize: 12, color: '#6b7280' }}>Renewal preserves previous expiry history. Bank limit utilization adjusts only if amount or bank limit changes.</p>
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="button" className="btn-primary" onClick={handleSubmit} disabled={saving || uploading}>{saving ? 'Renewing...' : 'Renew BG / Bond'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BgRenewModal;
